import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Typography, Select, MenuItem, FormControl, Grid, CircularProgress, Alert, TextField } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { useNavItems } from '../../../hooks';
import { useModuleOperators } from '../../../hooks/useModuleOperators';
import { useCN19Report } from './hooks';
import NotificationSnackbar from '../../../common/components/Snackbar/Snackbar';
import IbrsRateTable from './components/IbrsRateTable';
import { CN19Table } from './components';
import { IbrsRateData, parseCSV, convertToCSV, downloadCSV } from '../../../utils/csvUtils';
import { useUser } from '../../../hooks/useUser';
import { 
  MainContent,
  ReportContainer,
  FilterSection,
  SelectContainer,
  ExportButton
} from './CN19Report.styled';

function CN19Report() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const navItems = useNavItems();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // CN19 specific state
  const [cn19Ppo, setCn19Ppo] = useState('All');
  const [cn19Year, setCn19Year] = useState('');
  const [cn19Currency, setCn19Currency] = useState('EUR');

  // Inbound IBRS Rate table state
  const [inboundNormalIbrsWeight, setInboundNormalIbrsWeight] = useState('');
  const [inboundNormalIbrsItems, setInboundNormalIbrsItems] = useState('');
  const [inboundBulkyIbrsWeight, setInboundBulkyIbrsWeight] = useState('');
  const [inboundBulkyIbrsItems, setInboundBulkyIbrsItems] = useState('');

  // Outbound IBRS Rate table state
  const [outboundNormalIbrsWeight, setOutboundNormalIbrsWeight] = useState('');
  const [outboundNormalIbrsItems, setOutboundNormalIbrsItems] = useState('');
  const [outboundBulkyIbrsWeight, setOutboundBulkyIbrsWeight] = useState('');
  const [outboundBulkyIbrsItems, setOutboundBulkyIbrsItems] = useState('');

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'warning' | 'info' | 'success'
  });

  // Validation alert state
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  
  // CN19 report data state
  const [cn19ReportData, setCn19ReportData] = useState<any[]>([]);
  
  // Track which fields have been touched/interacted with
  const [touchedFields, setTouchedFields] = useState({
    ppo: false,
    year: false,
    currency: false,
    inboundNormalIbrsWeight: false,
    inboundNormalIbrsItems: false,
    inboundBulkyIbrsWeight: false,
    inboundBulkyIbrsItems: false,
    outboundNormalIbrsWeight: false,
    outboundNormalIbrsItems: false,
    outboundBulkyIbrsWeight: false,
    outboundBulkyIbrsItems: false,
  });

  // Get effective operators and cascading logic for e09 module
  const {
    operators,
    availableYears,
    operatorsForYear,
    yearsForOperator,
    relationsData: allRelationsData,
    isPpoLoading,
    ppoError,
  } = useModuleOperators('e09', { selectedYear: cn19Year, selectedPpo: cn19Ppo });

  // Get current user info
  const { data: userInfo } = useUser();
  
  // CN19 Report hook
  const {
    mutate: submitCN19Request,
    isPending: isProcessing,
    error: reportError,
    reset: resetReportError
  } = useCN19Report();

  // Map the target DOs to PPO options with cascading logic
  const ppoOptions = React.useMemo(() => {
    const options: { id: string; label: string; hasLicense?: boolean }[] = [];
    const targetDOsToUse = cn19Year ? operatorsForYear : operators;
    targetDOsToUse.forEach((targetDO) => {
      options.push({ id: targetDO, label: targetDO, hasLicense: true });
    });
    return options;
  }, [operators, operatorsForYear, cn19Year]);

  // Generate year options with cascading logic
  const yearOptions = React.useMemo(() => {
    const yearsToShow = cn19Ppo && cn19Ppo !== 'All' ? yearsForOperator : availableYears;
    return yearsToShow.map(year => year.toString());
  }, [availableYears, yearsForOperator, cn19Ppo]);

  const handleSidebarToggle = () => {
    setSidebarOpen(prev => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/e09');
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showSnackbar = (message: string, severity: 'error' | 'warning' | 'info' | 'success' = 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const getMissingFields = () => {
    const missingFields: string[] = [];
    
    if (!cn19Ppo) {
      missingFields.push('PPO');
    }
    if (!cn19Year) {
      missingFields.push('Year');
    }
    if (!cn19Currency.trim()) {
      missingFields.push('Currency');
    }

    // Check IBRS table fields
    if (!inboundNormalIbrsWeight.trim()) {
      missingFields.push('Inbound Normal IBRS Weight');
    }
    if (!inboundNormalIbrsItems.trim()) {
      missingFields.push('Inbound Normal IBRS Items');
    }
    if (!inboundBulkyIbrsWeight.trim()) {
      missingFields.push('Inbound Bulky IBRS Weight');
    }
    if (!inboundBulkyIbrsItems.trim()) {
      missingFields.push('Inbound Bulky IBRS Items');
    }
    if (!outboundNormalIbrsWeight.trim()) {
      missingFields.push('Outbound Normal IBRS Weight');
    }
    if (!outboundNormalIbrsItems.trim()) {
      missingFields.push('Outbound Normal IBRS Items');
    }
    if (!outboundBulkyIbrsWeight.trim()) {
      missingFields.push('Outbound Bulky IBRS Weight');
    }
    if (!outboundBulkyIbrsItems.trim()) {
      missingFields.push('Outbound Bulky IBRS Items');
    }

    return missingFields;
  };

  const validateAndShowErrors = () => {
    const missingFields = getMissingFields();

    if (missingFields.length > 0) {
      setShowValidationAlert(true);
      // Mark all fields as touched when validation fails
      setTouchedFields({
        ppo: true,
        year: true,
        currency: true,
        inboundNormalIbrsWeight: true,
        inboundNormalIbrsItems: true,
        inboundBulkyIbrsWeight: true,
        inboundBulkyIbrsItems: true,
        outboundNormalIbrsWeight: true,
        outboundNormalIbrsItems: true,
        outboundBulkyIbrsWeight: true,
        outboundBulkyIbrsItems: true,
      });
      return false;
    }
    
    setShowValidationAlert(false);
    return true;
  };

  // CN19 Handler functions
  const handleCn19PpoChange = useCallback((ppo: string) => {
    setCn19Ppo(ppo);
  }, []);

  const handleCn19YearChange = useCallback((year: string) => {
    setCn19Year(year);
  }, []);

  const handleCn19CurrencyChange = useCallback((currency: string) => {
    setCn19Currency(currency);
  }, []);

  // Handle DO selection change — years are independent of the selected DO
  const handleDOChange = useCallback((value: string) => {
    setCn19Ppo(value);
  }, []);

  // Handle year selection change with cascading behavior
  const handleYearChange = useCallback((value: string) => {
    // Only clear DO selection if the currently selected DO is not available for the new year
    if (cn19Year !== value && cn19Ppo && cn19Ppo !== 'All') {
      // Check if the current DO is available for the new year
      const relationsForNewYear = allRelationsData?.byYear?.[value];
      const isCurrentDOAvailableForNewYear = relationsForNewYear?.some(relation => 
        relation.targetDO === cn19Ppo &&
        relation.modules.some(module => 
          module.name === 'e09' && module.enabled === true
        )
      );
      
      if (!isCurrentDOAvailableForNewYear) {
        setCn19Ppo('All');
      }
    }
    setCn19Year(value);
  }, [cn19Year, cn19Ppo, allRelationsData]);

  // Validation helper
  const isFormValid = React.useMemo(() => {
    return cn19Ppo && 
           cn19Year && 
           cn19Currency.trim() &&
           inboundNormalIbrsWeight.trim() &&
           inboundNormalIbrsItems.trim() &&
           inboundBulkyIbrsWeight.trim() &&
           inboundBulkyIbrsItems.trim() &&
           outboundNormalIbrsWeight.trim() &&
           outboundNormalIbrsItems.trim() &&
           outboundBulkyIbrsWeight.trim() &&
           outboundBulkyIbrsItems.trim();
  }, [
    cn19Ppo, 
    cn19Year, 
    cn19Currency,
    inboundNormalIbrsWeight,
    inboundNormalIbrsItems,
    inboundBulkyIbrsWeight,
    inboundBulkyIbrsItems,
    outboundNormalIbrsWeight,
    outboundNormalIbrsItems,
    outboundBulkyIbrsWeight,
    outboundBulkyIbrsItems
  ]);

  // Hide validation alert when form becomes valid 
  React.useEffect(() => {
    if (isFormValid && showValidationAlert) {
      setShowValidationAlert(false);
    }
  }, [isFormValid, showValidationAlert]);

  // Handle reportError through snackbar
  React.useEffect(() => {
    if (reportError) {
      showSnackbar(reportError.message || 'An error occurred while processing the request');
    }
  }, [reportError]);

  // Sync URL params with page state
  useEffect(() => {
    const ppoParam = searchParams.get('ppo');
    const yearParam = searchParams.get('year');

    // Sync PPO
    if (ppoParam && ppoParam !== cn19Ppo) {
      setCn19Ppo(ppoParam);
    }

    // Sync year
    if (yearParam && yearParam !== cn19Year) {
      setCn19Year(yearParam);
    }
  }, [searchParams, cn19Ppo, cn19Year]);

  // Helper function to build CN19 API parameters
  const buildCN19Params = (formatType: string) => {
    return {
      formatType,
      designatedOperator: cn19Ppo,
      year: cn19Year ? parseInt(cn19Year) : undefined,
      currency: cn19Currency,
      inbound: {
        normalIbrs: {
          weight: inboundNormalIbrsWeight ? parseFloat(inboundNormalIbrsWeight) : undefined,
          items: inboundNormalIbrsItems ? parseFloat(inboundNormalIbrsItems) : undefined,
        },
        bulkyIbrs: {
          weight: inboundBulkyIbrsWeight ? parseFloat(inboundBulkyIbrsWeight) : undefined,
          items: inboundBulkyIbrsItems ? parseFloat(inboundBulkyIbrsItems) : undefined,
        },
      },
      outbound: {
        normalIbrs: {
          weight: outboundNormalIbrsWeight ? parseFloat(outboundNormalIbrsWeight) : undefined,
          items: outboundNormalIbrsItems ? parseFloat(outboundNormalIbrsItems) : undefined,
        },
        bulkyIbrs: {
          weight: outboundBulkyIbrsWeight ? parseFloat(outboundBulkyIbrsWeight) : undefined,
          items: outboundBulkyIbrsItems ? parseFloat(outboundBulkyIbrsItems) : undefined,
        },
      },
    };
  };

  // CSV upload handler
  const handleCsvUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = parseCSV(text);
      
      if (data.length === 0) {
        showSnackbar('CSV file contains no valid data rows', 'error');
        return;
      }

      // Get current user's designated operator from user context
      const currentUserPpo = userInfo?.designatedOperator;
      if (!currentUserPpo) {
        showSnackbar('Unable to determine current user PPO', 'error');
        return;
      }

      console.log('CSV Upload Debug:', {
        currentUserPpo,
        csvData: data,
        firstRow: data[0]
      });

      // Find the other PPO in the bidirectional relationship
      // Look for any PPO that is different from the current user's PPO
      let otherPpo: string | undefined;
      
      for (const row of data) {
        if (row.sending_ppo !== currentUserPpo) {
          otherPpo = row.sending_ppo;
          break;
        } else if (row.receiving_ppo !== currentUserPpo) {
          otherPpo = row.receiving_ppo;
          break;
        }
      }

      if (!otherPpo) {
        showSnackbar('No valid PPO found in CSV data for bidirectional relationship', 'error');
        return;
      }

      console.log('Found other PPO:', otherPpo);

      // Automatically set the PPO dropdown to the other PPO from the CSV
      setCn19Ppo(otherPpo);

      // Find rates for the other PPO (both sending and receiving)
      const ppoRates = data.filter(row => 
        row.sending_ppo === otherPpo || row.receiving_ppo === otherPpo
      );

      if (ppoRates.length === 0) {
        showSnackbar(`No IBRS rates found for PPO: ${otherPpo}`, 'error');
        return;
      }

      // Find inbound rates (where other PPO is sending TO current user PPO - inbound from current user's perspective)
      const inboundRates = ppoRates.filter(row => row.sending_ppo === otherPpo && row.receiving_ppo === currentUserPpo);
      
      // Find outbound rates (where current user PPO is sending TO other PPO - outbound from current user's perspective)
      const outboundRates = ppoRates.filter(row => row.sending_ppo === currentUserPpo && row.receiving_ppo === otherPpo);

      // Update inbound rates
      if (inboundRates.length > 0) {
        const normalInbound = inboundRates.find(row => row.product === 'normal');
        const bulkyInbound = inboundRates.find(row => row.product === 'bulky');

        setInboundNormalIbrsWeight((normalInbound?.ibrs_rate_kg || 0).toFixed(3));
        setInboundNormalIbrsItems((normalInbound?.ibrs_rate_item || 0).toFixed(3));
        setInboundBulkyIbrsWeight((bulkyInbound?.ibrs_rate_kg || 0).toFixed(3));
        setInboundBulkyIbrsItems((bulkyInbound?.ibrs_rate_item || 0).toFixed(3));
        
        // Mark fields as touched
        setTouchedFields(prev => ({
          ...prev,
          inboundNormalIbrsWeight: true,
          inboundNormalIbrsItems: true,
          inboundBulkyIbrsWeight: true,
          inboundBulkyIbrsItems: true,
        }));
      }

      // Update outbound rates
      if (outboundRates.length > 0) {
        const normalOutbound = outboundRates.find(row => row.product === 'normal');
        const bulkyOutbound = outboundRates.find(row => row.product === 'bulky');

        setOutboundNormalIbrsWeight((normalOutbound?.ibrs_rate_kg || 0).toFixed(3));
        setOutboundNormalIbrsItems((normalOutbound?.ibrs_rate_item || 0).toFixed(3));
        setOutboundBulkyIbrsWeight((bulkyOutbound?.ibrs_rate_kg || 0).toFixed(3));
        setOutboundBulkyIbrsItems((bulkyOutbound?.ibrs_rate_item || 0).toFixed(3));
        
        // Mark fields as touched
        setTouchedFields(prev => ({
          ...prev,
          outboundNormalIbrsWeight: true,
          outboundNormalIbrsItems: true,
          outboundBulkyIbrsWeight: true,
          outboundBulkyIbrsItems: true,
        }));
      }

      showSnackbar('CSV file uploaded successfully and IBRS rates updated', 'success');
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process CSV file';
      showSnackbar(errorMessage, 'error');
    }
  }, [showSnackbar, userInfo]);

  // Download current IBRS rates as CSV
  const handleDownloadIbrsRates = useCallback(() => {
    if (!cn19Ppo || cn19Ppo === 'All') {
      showSnackbar('Please select a PPO first to download IBRS rates', 'error');
      return;
    }

    // Get current user's designated operator from user context
    const currentUserPpo = userInfo?.designatedOperator;
    
    if (!currentUserPpo) {
      showSnackbar('Unable to determine current user PPO', 'error');
      return;
    }
    
    const currentRates: IbrsRateData[] = [];
    
    // Create bidirectional rates between selected PPO and current user PPO
    
    // Inbound rates: Selected PPO sending TO current user (inbound from current user's perspective)
    if (inboundNormalIbrsWeight || inboundNormalIbrsItems) {
      currentRates.push({
        sending_ppo: cn19Ppo,
        receiving_ppo: currentUserPpo,
        product: 'normal',
        ibrs_rate_kg: parseFloat(inboundNormalIbrsWeight) || 0,
        ibrs_rate_item: parseFloat(inboundNormalIbrsItems) || 0
      });
    }
    
    if (inboundBulkyIbrsWeight || inboundBulkyIbrsItems) {
      currentRates.push({
        sending_ppo: cn19Ppo,
        receiving_ppo: currentUserPpo,
        product: 'bulky',
        ibrs_rate_kg: parseFloat(inboundBulkyIbrsWeight) || 0,
        ibrs_rate_item: parseFloat(inboundBulkyIbrsItems) || 0
      });
    }
    
    // Outbound rates: Current user sending TO selected PPO (outbound from current user's perspective)
    if (outboundNormalIbrsWeight || outboundNormalIbrsItems) {
      currentRates.push({
        sending_ppo: currentUserPpo,
        receiving_ppo: cn19Ppo,
        product: 'normal',
        ibrs_rate_kg: parseFloat(outboundNormalIbrsWeight) || 0,
        ibrs_rate_item: parseFloat(outboundNormalIbrsItems) || 0
      });
    }
    
    if (outboundBulkyIbrsWeight || outboundBulkyIbrsItems) {
      currentRates.push({
        sending_ppo: currentUserPpo,
        receiving_ppo: cn19Ppo,
        product: 'bulky',
        ibrs_rate_kg: parseFloat(outboundBulkyIbrsWeight) || 0,
        ibrs_rate_item: parseFloat(outboundBulkyIbrsItems) || 0
      });
    }

    if (currentRates.length === 0) {
      showSnackbar('No IBRS rates to download', 'warning');
      return;
    }

    const csvContent = convertToCSV(currentRates);
    const filename = `ibrs_rates_${currentUserPpo}_${cn19Ppo}_${cn19Year}.csv`;
    downloadCSV(csvContent, filename);
    showSnackbar('IBRS rates downloaded successfully', 'success');
  }, [
    cn19Ppo, 
    inboundNormalIbrsWeight, 
    inboundNormalIbrsItems, 
    inboundBulkyIbrsWeight, 
    inboundBulkyIbrsItems,
    outboundNormalIbrsWeight, 
    outboundNormalIbrsItems, 
    outboundBulkyIbrsWeight, 
    outboundBulkyIbrsItems,
    showSnackbar
  ]);



  // CN19 Handler functions for different actions
  const handleCN19Action = useCallback((formatType: string, actionName: string) => {
    // Reset any previous errors
    resetReportError();

    if (!validateAndShowErrors()) {
      return;
    }

    const params = buildCN19Params(formatType);

    submitCN19Request(params, {
      onSuccess: (response: any) => {
        showSnackbar(response.message || `${actionName} completed successfully`, 'success');
        console.log(`${actionName} successful:`, response);
        
        // Only store the response data for display in CN19Table if it's HTML format
        if (formatType === 'html') {
          if (response.data && Array.isArray(response.data)) {
            setCn19ReportData(response.data);
          } else if (response.data) {
            setCn19ReportData([response.data]);
          }
        }
        // For PDF/other formats, the file download is handled in the hook
      },
      onError: async (error: Error) => {
        console.error(`Error with ${actionName}:`, error);
        
        // Handle NOT_FOUND errors for PDF exports
        if (formatType !== 'html') {
          let errorMessage = `Failed to ${actionName.toLowerCase()}`;
          let errorStatus: string | undefined;
          
          // Check if it's an axios error
          if (axios.isAxiosError(error)) {
            // For blob responses, the error data might be a Blob containing JSON
            if (error.response?.data instanceof Blob) {
              try {
                const text = await error.response.data.text();
                const errorData = JSON.parse(text);
                errorMessage = errorData.message || errorMessage;
                errorStatus = errorData.status;
              } catch (parseError) {
                // If parsing fails, use default message
                console.error('Failed to parse error response:', parseError);
              }
            } else if (error.response?.data && typeof error.response.data === 'object') {
              // For non-blob responses, check the data directly
              const errorData = error.response.data as { message?: string; status?: string };
              errorMessage = errorData.message || errorMessage;
              errorStatus = errorData.status;
            }
            
            // Check status code as fallback
            if (!errorStatus && error.response?.status === 404) {
              errorStatus = 'NOT_FOUND';
            }
          }
          
          // Show snackbar with appropriate message
          if (errorStatus === 'NOT_FOUND' || errorMessage.includes('No CN19 reports available') || errorMessage.includes('No CN19')) {
            showSnackbar(
              errorMessage || 'No CN19 reports available for the selected criteria',
              'warning'
            );
          } else {
            showSnackbar(errorMessage, 'error');
          }
        } else {
          // For HTML format, show error snackbar
          showSnackbar(
            error instanceof Error ? error.message : `Failed to ${actionName.toLowerCase()}`,
            'error'
          );
        }
      }
    });
  }, [
    cn19Ppo, 
    cn19Year, 
    inboundNormalIbrsWeight,
    inboundNormalIbrsItems,
    inboundBulkyIbrsWeight,
    inboundBulkyIbrsItems,
    outboundNormalIbrsWeight,
    outboundNormalIbrsItems,
    outboundBulkyIbrsWeight,
    outboundBulkyIbrsItems,
    submitCN19Request, 
    resetReportError,
    validateAndShowErrors
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='cn19-report-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='CN19 Report'
            subtitle='e09 IBRS'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            onSubtitleClick={handleSubtitleClick}
            data-testid="section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="cn19-report-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='cn19-report-main-content'>
            <ReportContainer data-testid="cn19-report-content">
              {/* Header */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 3 
              }} data-testid="cn19-report-header">
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#063142' }} data-testid="cn19-report-title">
                  CN19 Report
                </Typography>
              </Box>

              {/* CN19 Filter Section */}
              <FilterSection data-testid="cn19-report-filters">
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 3,
                  alignItems: { xs: 'stretch', md: 'flex-end' }
                }}>
                  {/* PPO Dropdown */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: '200px' }} data-testid="cn19-report-ppo-section">
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                      DO <span style={{ color: '#d32f2f' }}>*</span>
                    </Typography>
                    <SelectContainer>
                      <FormControl size="small" fullWidth required>
                        <Select
                          value={cn19Ppo}
                          onChange={(e) => {
                            handleDOChange(e.target.value);
                            setTouchedFields(prev => ({ ...prev, ppo: true }));
                          }}
                          displayEmpty
                          disabled={isPpoLoading}
                          error={touchedFields.ppo && !cn19Ppo}
                          data-testid="cn19-report-ppo-select"
                        >
                          <MenuItem value="" disabled>
                            Select DO
                          </MenuItem>
                          {isPpoLoading && (
                            <MenuItem value="" disabled>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} />
                                <span>Loading...</span>
                              </Box>
                            </MenuItem>
                          )}
                          {!isPpoLoading && !ppoError && ppoOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <span>{option.label}</span>
                              </Box>
                            </MenuItem>
                          ))}
                          {ppoError && (
                            <MenuItem disabled>Error loading PPOs</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </SelectContainer>
                  </Box>
                  
                  {/* Year Dropdown */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: '150px' }} data-testid="cn19-report-year-section">
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                      Year <span style={{ color: '#d32f2f' }}>*</span>
                    </Typography>
                    <SelectContainer>
                      <FormControl size="small" fullWidth required>
                        <Select
                          value={cn19Year}
                          onChange={(e) => {
                            handleYearChange(e.target.value);
                            setTouchedFields(prev => ({ ...prev, year: true }));
                          }}
                          displayEmpty
                          error={touchedFields.year && !cn19Year}
                          data-testid="cn19-report-year-select"
                        >
                          <MenuItem value="" disabled>Select Year</MenuItem>
                          {yearOptions.map((year) => (
                            <MenuItem key={year} value={year}>
                              {year}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </SelectContainer>
                  </Box>

                  {/* Currency Input */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: '150px' }} data-testid="cn19-report-currency-section">
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                      Currency <span style={{ color: '#d32f2f' }}>*</span>
                    </Typography>
                    <TextField
                      size="small"
                      value={cn19Currency}
                      onChange={(e) => {
                        setCn19Currency(e.target.value);
                        setTouchedFields(prev => ({ ...prev, currency: true }));
                      }}
                      placeholder="Enter currency"
                      required
                      error={touchedFields.currency && !cn19Currency.trim()}
                      data-testid="cn19-report-currency-input"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: '32px',
                          fontSize: '14px',
                          backgroundColor: '#FFFFFF',
                          '& fieldset': {
                            borderColor: '#D1D9E0',
                          },
                          '&:hover fieldset': {
                            borderColor: '#00ACE7',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#00ACE7',
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: '8px 14px',
                        },
                      }}
                    />
                  </Box>


                </Box>
              </FilterSection>

              {/* IBRS Rate Tables - Side by Side */}
              <Box sx={{ 
                display: 'flex', 
                gap: 3, 
                flexDirection: { xs: 'column', md: 'row' },
                mt: 3,
                maxWidth: '800px',
              }} data-testid="cn19-report-ibrs-tables">
                {/* Inbound IBRS Rate Table */}
                <Box sx={{ flex: 1 }} data-testid="cn19-report-inbound-ibrs">
                  <IbrsRateTable
                    title="Inbound"
                    type="inbound"
                    normalIbrsWeight={inboundNormalIbrsWeight}
                    normalIbrsItems={inboundNormalIbrsItems}
                    bulkyIbrsWeight={inboundBulkyIbrsWeight}
                    bulkyIbrsItems={inboundBulkyIbrsItems}
                    onNormalIbrsWeightChange={(value) => {
                      setInboundNormalIbrsWeight(value);
                      setTouchedFields(prev => ({ ...prev, inboundNormalIbrsWeight: true }));
                    }}
                    onNormalIbrsItemsChange={(value) => {
                      setInboundNormalIbrsItems(value);
                      setTouchedFields(prev => ({ ...prev, inboundNormalIbrsItems: true }));
                    }}
                    onBulkyIbrsWeightChange={(value) => {
                      setInboundBulkyIbrsWeight(value);
                      setTouchedFields(prev => ({ ...prev, inboundBulkyIbrsWeight: true }));
                    }}
                    onBulkyIbrsItemsChange={(value) => {
                      setInboundBulkyIbrsItems(value);
                      setTouchedFields(prev => ({ ...prev, inboundBulkyIbrsItems: true }));
                    }}
                    normalIbrsWeightError={touchedFields.inboundNormalIbrsWeight && !inboundNormalIbrsWeight.trim()}
                    normalIbrsItemsError={touchedFields.inboundNormalIbrsItems && !inboundNormalIbrsItems.trim()}
                    bulkyIbrsWeightError={touchedFields.inboundBulkyIbrsWeight && !inboundBulkyIbrsWeight.trim()}
                    bulkyIbrsItemsError={touchedFields.inboundBulkyIbrsItems && !inboundBulkyIbrsItems.trim()}
                  />
                </Box>

                {/* Outbound IBRS Rate Table */}
                <Box sx={{ flex: 1 }} data-testid="cn19-report-outbound-ibrs">
                  <IbrsRateTable
                    title="Outbound"
                    type="outbound"
                    normalIbrsWeight={outboundNormalIbrsWeight}
                    normalIbrsItems={outboundNormalIbrsItems}
                    bulkyIbrsWeight={outboundBulkyIbrsWeight}
                    bulkyIbrsItems={outboundBulkyIbrsItems}
                    onNormalIbrsWeightChange={(value) => {
                      setOutboundNormalIbrsWeight(value);
                      setTouchedFields(prev => ({ ...prev, outboundNormalIbrsWeight: true }));
                    }}
                    onNormalIbrsItemsChange={(value) => {
                      setOutboundNormalIbrsItems(value);
                      setTouchedFields(prev => ({ ...prev, outboundNormalIbrsItems: true }));
                    }}
                    onBulkyIbrsWeightChange={(value) => {
                      setOutboundBulkyIbrsWeight(value);
                      setTouchedFields(prev => ({ ...prev, outboundBulkyIbrsWeight: true }));
                    }}
                    onBulkyIbrsItemsChange={(value) => {
                      setOutboundBulkyIbrsItems(value);
                      setTouchedFields(prev => ({ ...prev, outboundBulkyIbrsItems: true }));
                    }}
                    normalIbrsWeightError={touchedFields.outboundNormalIbrsWeight && !outboundNormalIbrsWeight.trim()}
                    normalIbrsItemsError={touchedFields.outboundNormalIbrsItems && !outboundNormalIbrsItems.trim()}
                    bulkyIbrsWeightError={touchedFields.outboundBulkyIbrsWeight && !outboundBulkyIbrsWeight.trim()}
                    bulkyIbrsItemsError={touchedFields.outboundBulkyIbrsItems && !outboundBulkyIbrsItems.trim()}
                  />
                </Box>
              </Box>

              {/* Validation Alert */}
              {showValidationAlert && (
                <Alert 
                  severity="error" 
                  sx={{ mt: 3 }}
                  onClose={() => setShowValidationAlert(false)}
                  data-testid="cn19-report-validation-alert"
                >
                  Please fill in all required fields: {getMissingFields().join(', ')}
                </Alert>
              )}

              {/* Hidden file input for CSV upload */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
                style={{ display: 'none' }}
              />

              {/* Action Buttons */}
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: 'flex-start',
                mt: 4,
                flexWrap: 'wrap'
              }} data-testid="cn19-report-action-buttons">
                <ExportButton 
                  variant="contained"
                  disabled={isProcessing}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="cn19-report-upload-btn"
                >
                  Upload IBRS Rates
                </ExportButton>
                
                <ExportButton 
                  variant="contained"
                  disabled={isProcessing}
                  onClick={handleDownloadIbrsRates}
                  data-testid="cn19-report-download-btn"
                >
                  Download IBRS Rates
                </ExportButton>
                
                <ExportButton 
                  variant="contained"
                  disabled={isProcessing}
                  onClick={() => handleCN19Action('html', 'HTML Export')}
                  data-testid="cn19-report-html-btn"
                >
                  HTML
                </ExportButton>
                
                <ExportButton 
                  variant="contained"
                  disabled={isProcessing}
                  onClick={() => handleCN19Action('pdf', 'PDF Export')}
                  data-testid="cn19-report-pdf-btn"
                >
                  PDF
                </ExportButton>
              </Box>

              {/* CN19 Report Results Table */}
              {cn19ReportData.length > 0 && (
                <Box sx={{ mt: 4 }} data-testid="cn19-report-results">
                  <CN19Table data={cn19ReportData} data-testid="cn19-report-results-table" />
                </Box>
              )}
            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid="cn19-report-snackbar"
      />
    </Box>
  );
}

export default CN19Report; 